import { computed } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import pluralize from 'pluralize'

import {
  CopyrightStatus,
  type File,
  type Item,
} from '@/shared/graphql/router/graphql'
import { useExplorerStore, type CurrentFolder } from '@/features/explorer/store'
import { isFile } from '@/features/explorer/utils/itemGuards'
import { fileTypeToThumbnail } from '@/shared/utils/thumbnail'
import { formatTimestamp } from '@/shared/utils/datetime'
import { applyAuthorizationToken } from '@/shared/utils/url'
import { truncateMiddle } from '@/shared/utils/string'
import { openDialog, Dialog } from '@/shared/dialogs/dialog'
import { useExplorerSelection } from '@/features/explorer/composables/useExplorerSelection'

import type { SidebarModel, SidebarRow, SidebarThumbnail } from './types'

type SidebarItem = Item | CurrentFolder

export function useSidebarModel() {
  const route = useRoute()
  const router = useRouter()
  const explorerStore = useExplorerStore()
  const { selectedItems } = useExplorerSelection()

  const multiple = computed(() => selectedItems.value.length > 1)

  const item = computed<SidebarItem | null>(() =>
    selectedItems.value.length
      ? selectedItems.value[0]
      : explorerStore.currentFolder
  )

  const fileItem = computed<File | null>(() => {
    const value = item.value
    return value && '__typename' in value && value.__typename === 'File'
      ? (value as File)
      : null
  })

  const inactive = (file: File) =>
    file.copyright?.status === CopyrightStatus.Inactive

  const imageUrl = (url?: string | null) =>
    url ? applyAuthorizationToken(url, [{ key: 'width', value: 300 }]) : null

  const locatedPath = computed(() => {
    const segments = (route.params.path as string[] | undefined) ?? []
    if (segments.length === 0) return null
    const showParent = selectedItems.value.length === 0
    return ['Dacr', ...(showParent ? segments.slice(0, -1) : segments)]
  })

  const navigate = (path: string[]) => {
    if (path.length === 1) {
      router.push({ name: 'FileExplorerRootFolderView' })
    } else {
      router.push({
        name: 'FileExplorerFolderView',
        params: { path: path.slice(1) },
      })
    }
  }

  const owners = (file: File) => {
    const names: string[] = []
    if (file.owner?.name) names.push(file.owner.name)
    file.coOwners?.edges?.forEach((edge) => {
      if (edge.node.__typename === 'EntityWithRole') {
        names.push(edge.node.entity.name)
      }
    })
    return names
  }

  const authors = (file: File) => {
    const names: string[] = []
    file.authors?.edges?.forEach((edge) => {
      if (edge.node.__typename === 'EntityWithRole') {
        names.push(edge.node.entity.name)
      }
    })
    return names
  }

  const visibility = (file: File) => {
    if (inactive(file)) return '—'
    return file.settings?.isPrivate ? 'Private' : 'Public'
  }

  const expiration = (file: File) => {
    if (inactive(file)) return '—'
    const expiresAt = file.copyright?.registrations?.[0]?.expiresAt
    return expiresAt
      ? (formatTimestamp(expiresAt) ?? 'Indefinite')
      : 'Indefinite'
  }

  const timestampRow = (file: File): SidebarRow => {
    if (inactive(file)) return { kind: 'text', label: 'Timestamp', value: '—' }
    const items = (file.copyright?.registrations ?? []).map(
      (registration, index) => ({
        id: `${registration.origin?.id ?? index}-${registration.timestamp ?? index}`,
        date: formatTimestamp(registration.timestamp ?? 0) ?? '',
        flagUrl: registration.origin?.flag?.url,
        certificateUrl: registration.certificateUrl,
      })
    )
    return { kind: 'registrations', label: 'Timestamp', items }
  }

  const thumbnail = computed<SidebarThumbnail>(() => {
    if (multiple.value) {
      return {
        type: 'generic',
        stack: selectedItems.value.slice(-3).map((selected) => ({
          id: selected.id,
          type: isFile(selected)
            ? fileTypeToThumbnail(selected.type)
            : 'folder',
          src: isFile(selected)
            ? imageUrl(selected.asset?.previewImageUrl)
            : null,
        })),
      }
    }

    const file = fileItem.value
    if (file) {
      return {
        type: fileTypeToThumbnail(file.type),
        src: imageUrl(file.asset?.previewImageUrl),
        onClick: inactive(file)
          ? undefined
          : () => router.push(`/file/${file.copyright?.id}`),
      }
    }

    return { type: 'folder' }
  })

  const rows = computed<SidebarRow[]>(() => {
    const value = item.value
    if (!value || multiple.value) return []

    const file = fileItem.value
    if (file) {
      const authorNames = authors(file)
      const ownerNames = owners(file)
      const result: SidebarRow[] = []

      if (locatedPath.value) {
        result.push({
          kind: 'breadcrumb',
          label: 'Located in',
          path: locatedPath.value,
          onNavigate: navigate,
        })
      }

      result.push(
        timestampRow(file),
        {
          kind: 'text',
          label: pluralize('Owner', ownerNames.length),
          value: ownerNames.join(', '),
        },
        {
          kind: 'text',
          label: pluralize('Author', authorNames.length),
          value: authorNames.join(', ') || '—',
        },
        {
          kind: 'text',
          label: 'Registrant',
          value: file.registrant?.name ?? '—',
        },
        {
          kind: 'text',
          label: 'Size',
          value: String(file.metadata?.size ?? ''),
        },
        { kind: 'text', label: 'Type', value: file.type },
        { kind: 'text', label: 'Visibility', value: visibility(file) },
        {
          kind: 'text',
          label: 'Copyright protection expiration',
          value: expiration(file),
        },
        {
          kind: 'link',
          label: 'Edit copyright settings',
          onClick: () => openDialog(Dialog.FileSettings, { props: { file } }),
        }
      )

      return result
    }

    const totalFiles = 'totalFiles' in value ? value.totalFiles : 0
    const createdAt = 'createdAt' in value ? value.createdAt : null
    const result: SidebarRow[] = []

    if (locatedPath.value) {
      result.push({
        kind: 'breadcrumb',
        label: 'Located in',
        path: locatedPath.value,
        onNavigate: navigate,
      })
    }

    result.push({
      kind: 'text',
      label: 'Number of registrations',
      value: String(totalFiles),
    })

    if (createdAt) {
      result.push({
        kind: 'text',
        label: 'Created',
        value: formatTimestamp(createdAt) ?? '',
      })
    }

    return result
  })

  const title = computed(() => {
    if (multiple.value) return `${selectedItems.value.length} selected`
    const file = fileItem.value
    if (file) return truncateMiddle(file.name, 25)
    const value = item.value
    return value?.name === '/' ? 'Dacr' : (value?.name ?? '')
  })

  const share = computed(() => {
    const file = fileItem.value
    if (!file || multiple.value) return null
    return {
      disabled: inactive(file),
      onClick: () => openDialog(Dialog.Share, { props: { file } }),
    }
  })

  const model = computed<SidebarModel | null>(() =>
    item.value
      ? {
          title: title.value,
          share: share.value,
          thumbnail: thumbnail.value,
          rows: rows.value,
        }
      : null
  )

  return { model, selectedItems }
}
